/* src/utils/fetchCityImage.ts
 * Re-written with stricter matching so only photos that
 * **explicitly mention the city AND look like a skyline**
 * make it through.  Works for Unsplash › Pixabay › Pexels
 * and still honours your manual overrides.
 */
import { cityImageOverrides } from '@/data/cityImageOverrides';

const CITYSCAPE_KEYWORDS = [
  'skyline',
  'scenic',
  'aerial',
  'architecture',
  'historic',
  'landmark',
  'coast',
  'mountain',
  'panorama',
  'city view'
];

const NEGATIVE_KEYWORDS = [
  'night',
  'dark',
  'portrait',
  'selfie',
  'people',
  'indoor',
  'interior'
];

const fallbackImage =
  'https://images.pexels.com/photos/3052361/pexels-photo-3052361.jpeg?auto=format&fit=crop&w=1600&q=80';

const matchesKeyword = (text: string, keywords: string[]) =>
  keywords.some(kw => text.includes(kw));

/* ────────────────────────────────────────────────────────── *
 * Helpers
 * ────────────────────────────────────────────────────────── */
const normalise = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // strip accents
    .toLowerCase();

const makeCityVariants = (cityKey: string): string[] => {
  const spaced = cityKey.replace(/_/g, ' ');
  const dashed = cityKey.replace(/_/g, '-');
  const plain = cityKey.replace(/_/g, '');
  return [cityKey, spaced, dashed, plain];
};

const cityAppearsIn = (txt: string, variants: string[]) => {
  const clean = normalise(txt);
  return variants.some(v => clean.includes(v));
};

// Clean and normalise the incoming city string once
const extractCityKey = (rawCity: string): string => {
  const cleaned = rawCity.split('/')[0].trim();
  return cleaned
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
};

/* ────────────────────────────────────────────────────────── *
 * Main
 * ────────────────────────────────────────────────────────── */
export const fetchCityImageFromMultipleSources = async (
  rawCity: string
): Promise<string> => {
  const cityKey = extractCityKey(rawCity);
  const cityVariants = makeCityVariants(cityKey);
  const searchTerm = `${cityKey.replace(/_/g, ' ')} skyline`;

  /* 0. Manual override ─────────────────────────────── */
  if (cityImageOverrides[cityKey]) {
    return cityImageOverrides[cityKey];
  }

  /* 1. Unsplash ────────────────────────────────────── */
  try {
    const res = await fetch(
      `https://api.unsplash.com/search/photos?query=${encodeURIComponent(
        searchTerm
      )}&orientation=landscape&per_page=20`,
      {
        headers: {
          Authorization: `Client-ID ${
            import.meta.env.VITE_UNSPLASH_API_KEY || ''
          }`
        }
      }
    );
    const data = await res.json();

    const valid = (data.results || []).filter((item: any) => {
      const alt = (item.alt_description || item.description || '').toLowerCase();
      const loc = (item.location?.title || '').toLowerCase();
      const blob = `${alt} ${loc}`;

      return (
        cityAppearsIn(blob, cityVariants) &&
        matchesKeyword(blob, CITYSCAPE_KEYWORDS) &&
        !matchesKeyword(blob, NEGATIVE_KEYWORDS)
      );
    });

    if (valid.length) {
      const pick = valid[Math.floor(Math.random() * valid.length)];
      return pick.urls.regular;
    }
  } catch (err) {
    console.warn('[CITY IMAGE] Unsplash failed:', err);
  }

  /* 2. Pixabay ─────────────────────────────────────── */
  try {
    const key = import.meta.env.VITE_PIXABAY_API_KEY || '';
    const res = await fetch(
      `https://pixabay.com/api/?key=${key}&q=${encodeURIComponent(
        searchTerm
      )}&image_type=photo&orientation=horizontal&per_page=20&safesearch=true`
    );
    const data = await res.json();

    const valid = (data.hits || []).filter((item: any) => {
      const tags = (item.tags || '').toLowerCase();

      return (
        cityAppearsIn(tags, cityVariants) &&
        matchesKeyword(tags, CITYSCAPE_KEYWORDS) &&
        !matchesKeyword(tags, NEGATIVE_KEYWORDS)
      );
    });

    if (valid.length) {
      const pick = valid[Math.floor(Math.random() * valid.length)];
      return pick.largeImageURL;
    }
  } catch (err) {
    console.warn('[CITY IMAGE] Pixabay failed:', err);
  }

  /* 3. Pexels ───────────────────────────────────────── */
  try {
    const res = await fetch(
      `https://api.pexels.com/v1/search?query=${encodeURIComponent(
        searchTerm
      )}&per_page=20&orientation=landscape`,
      {
        headers: { Authorization: import.meta.env.VITE_PEXELS_API_KEY || '' }
      }
    );
    const data = await res.json();

    const valid = (data.photos || []).filter((photo: any) => {
      const alt = (photo.alt || '').toLowerCase();

      return (
        cityAppearsIn(alt, cityVariants) &&
        matchesKeyword(alt, CITYSCAPE_KEYWORDS) &&
        !matchesKeyword(alt, NEGATIVE_KEYWORDS)
      );
    });

    if (valid.length) {
      const pick = valid[Math.floor(Math.random() * valid.length)];
      return pick.src.large2x;
    }
  } catch (err) {
    console.warn('[CITY IMAGE] Pexels failed:', err);
  }

  /* 4. Nothing worked → generic fallback */
  return fallbackImage;
};
